<template>
  <v-card class="notification-card elevation-0" color="transparent">
    <div class="notification-header px-4 py-3 d-flex align-center justify-space-between">
      <div>
        <h3 class="text-subtitle-1 font-weight-bold white--text mb-0">Benachrichtigungen</h3>
        <span class="caption grey--text text--lighten-1" v-if="notifications.length"> {{ unreadCount }} ungelesen </span>
      </div>

      <div class="d-flex">
        <v-tooltip bottom content-class="custom-tooltip">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-if="hasUnread" icon small v-bind="attrs" v-on="on" class="mr-1" @click="$emit('mark-all-read')">
              <v-icon color="primary" size="20">mdi-check-all</v-icon>
            </v-btn>
          </template>
          <span>Alle als gelesen markieren</span>
        </v-tooltip>

        <v-tooltip bottom content-class="custom-tooltip">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" @click="$router.push('/user')">
              <v-icon color="grey lighten-1" size="20">mdi-cog-outline</v-icon>
            </v-btn>
          </template>
          <span>Einstellungen</span>
        </v-tooltip>
      </div>
    </div>

    <v-divider class="glass-divider"></v-divider>

    <div class="notification-scroll-area">
      <div v-if="notifications.length === 0 && !showPushCta" class="empty-state pa-6 d-flex flex-column align-center justify-center text-center">
        <div class="empty-icon-wrapper mb-4">
          <v-icon size="32" color="grey lighten-2">mdi-bell-sleep-outline</v-icon>
        </div>
        <h4 class="white--text text-body-2 font-weight-bold mb-1">Alles ruhig hier</h4>
        <p class="caption grey--text mb-0" style="max-width: 200px">Du bist auf dem neuesten Stand. Keine neuen Nachrichten für dich.</p>
      </div>

      <v-list v-else class="notification-list py-0" color="transparent">
        <v-list-item
          v-for="(notification, index) in notifications"
          :key="notification._id || index"
          @click="handleClick(notification)"
          class="notification-item px-4 py-3"
          :class="{ 'unread-item': !notification.read }"
          ripple>
          <div class="status-indicator mr-3">
            <div v-if="!notification.read" class="unread-dot"></div>
          </div>

          <v-list-item-avatar class="ma-0 mr-4 align-self-start mt-1" size="40">
            <div class="icon-gradient-bg d-flex align-center justify-center" :class="getIconColorClass(notification)">
              <v-icon color="white" size="20">{{ getIcon(notification) }}</v-icon>
            </div>
          </v-list-item-avatar>

          <v-list-item-content class="py-1">
            <v-list-item-title class="text-body-2 font-weight-bold white--text mb-1 text-wrap" style="line-height: 1.4">
              {{ notification.title }}
            </v-list-item-title>
            <v-list-item-subtitle class="caption grey--text text--lighten-2 text-wrap mb-1" style="line-height: 1.4">
              {{ notification.text }}
            </v-list-item-subtitle>
            <v-list-item-subtitle class="super-caption primary--text text--lighten-2">
              {{ formatTime(notification.createdAt) }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </div>

    <div v-if="showPushCta" class="notification-footer pa-0">
      <v-divider class="glass-divider"></v-divider>
      <div class="push-alert pa-3 ma-2 rounded-lg d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon small color="white" class="mr-2">mdi-cellphone-message</v-icon>
          <span class="caption white--text font-weight-medium">Push aktivieren</span>
        </div>
        <v-btn x-small color="white" light class="font-weight-bold px-3" depressed @click="$emit('enable-push')"> Los </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script>
export default {
  props: {
    notifications: {
      type: Array,
      required: true,
      default: () => [],
    },
    showPushCta: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasUnread() {
      return this.notifications.some((n) => !n.read)
    },
    unreadCount() {
      return this.notifications.filter((n) => !n.read).length
    },
  },
  methods: {
    handleClick(notification) {
      this.$emit("notification-click", notification)
    },
    formatTime(date) {
      if (!date) return ""
      // Fallback falls moment nicht global verfügbar ist
      if (this.$moment) {
        return this.$moment(date).fromNow()
      }
      return new Date(date).toLocaleDateString("de-DE")
    },
    // Hilfsfunktion für Icons basierend auf Titel oder Typ (optional)
    getIcon(notification) {
      const title = (notification.title || "").toLowerCase()
      if (title.includes("kurs") || title.includes("training")) return "mdi-dumbbell"
      if (title.includes("vertrag") || title.includes("zahlung")) return "mdi-file-document-outline"
      if (title.includes("chat") || title.includes("nachricht")) return "mdi-message-text-outline"
      if (title.includes("willkommen")) return "mdi-party-popper"
      return "mdi-bell-outline"
    },
    // Hilfsfunktion für Farben
    getIconColorClass(notification) {
      if (!notification.read) return "gradient-primary"
      return "gradient-grey"
    },
  },
}
</script>

<style scoped lang="scss">
/* --- Variables & Glass Utils --- */
.glass-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* --- Layout --- */
.notification-card {
  display: flex;
  flex-direction: column;
  max-height: 80vh; /* Maximale Höhe */
  /* Für Mobile besser anpassen */
  @media (max-width: 600px) {
    max-height: 70vh;
  }
}

/* --- Scroll Area & Custom Scrollbar --- */
.notification-scroll-area {
  overflow-y: auto;
  flex-grow: 1;
  max-height: 400px;

  /* Webkit Scrollbar Styling (Chrome, Safari, Edge) */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.15);
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }
}

/* --- List Items --- */
.notification-item {
  transition: background-color 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);

  &:hover {
    background-color: rgba(255, 255, 255, 0.06);
  }

  &.unread-item {
    background-color: rgba(var(--v-primary-base), 0.08); /* Sehr subtiler Hintergrund für ungelesene */
  }
}

/* --- Typography --- */
.super-caption {
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

/* --- Indicators & Icons --- */
.status-indicator {
  width: 8px;
  display: flex;
  justify-content: center;
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--v-primary); /* Nutzt die Vuetify Primary Color */
  box-shadow: 0 0 8px var(--v-primary);
}

.icon-gradient-bg {
  width: 100%;
  height: 100%;
  border-radius: 50%;

  &.gradient-primary {
    background: linear-gradient(135deg, var(--v-primary), darken(#5658f3, 20%)); /* Passt sich deiner Primary Color an */
  }
  &.gradient-grey {
    background: linear-gradient(135deg, #424242, #212121);
  }
}

/* --- Empty State --- */
.empty-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* --- Footer CTA --- */
.push-alert {
  background: linear-gradient(90deg, var(--v-primary), #7c4dff);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* --- Tooltip Override (Global) --- */
::v-deep .custom-tooltip {
  background-color: rgba(30, 30, 30, 0.95);
  font-size: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
